import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import type { User } from "./types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, trusted: boolean) => Promise<void>;
  logout: () => Promise<void>;
  deviceLabel: string;
  setDeviceLabel: (value: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceLabel, updateDeviceLabel] = useState(
    localStorage.getItem("parallax-device-label") || ""
  );

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch((error) => {
        if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      deviceLabel,
      setDeviceLabel(value: string) {
        updateDeviceLabel(value);
        localStorage.setItem("parallax-device-label", value);
      },
      async login(username, password, trusted) {
        setUser(await api.login(username, password, trusted, deviceLabel));
      },
      async logout() {
        await api.logout();
        sessionStorage.removeItem("parallax-csrf");
        setUser(null);
      }
    }),
    [user, loading, deviceLabel]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

